import styled from "@emotion/styled";
import { InputAdornment, TextField } from "@mui/material";
import { matchSorter } from "match-sorter";
import { useCallback, useMemo, useState, type CSSProperties, type ChangeEventHandler } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { Icon } from "../icons";
import { FONT_WEIGHT_SEMI_BOLD, transientOptions } from "../utilities";
import { useSearch } from "./SearchContext";

const inputClassName = "search-box-input";
const htmlInputClassName = "search-box-html-input";

const SearchIcon = styled(Icon)`
  margin: 0 0 0 12px;
`;

const ClearIcon = styled(Icon, transientOptions)<{ $show: boolean }>`
  padding: 0 4px 0 0;

  opacity: ${({ $show }) => ($show ? 0.7 : 0)};
  pointer-events: ${({ $show }) => ($show ? "auto" : "none")};
  cursor: pointer;
  transition: opacity 300ms ease-in-out;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  height: 100%;
  border-radius: 10px;

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.outlineSoft};
    border-radius: 10px;
  }

  ${`& .${inputClassName}`} {
    padding: 0;
    height: 100%;
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
    font-size: var(--font-size-small, ${({ theme }) => theme.fontSize});
    background-color: ${({ theme }) => theme.surface};
    border-radius: 10px;
  }
  ${`& .${htmlInputClassName}`} {
    padding: 0;
    height: 100%;
  }
`;

const isObject = (data: unknown): data is object => typeof data === "object";

type SearchBoxProps<T> = Pick<CSSProperties, "height" | "borderRadius"> & {
  onlyKeys?: (keyof T)[];
  ignoreKeys?: (keyof T)[];
};

export const SearchBox = <T,>({ onlyKeys, ignoreKeys, ...rest }: SearchBoxProps<T>) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { source, processInput, reset } = useSearch<T>();

  const [value, setValue] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  const keys = useMemo(
    () =>
      source.length && isObject(source[0])
        ? Object.keys(source[0])
            .filter((key) => (ignoreKeys ? !ignoreKeys.includes(key as keyof T) : true))
            .filter((key) => (onlyKeys ? onlyKeys.includes(key as keyof T) : true))
            .map((key) => {
              return { key: key, threshold: matchSorter.rankings.CONTAINS };
            })
        : undefined,
    [source, ignoreKeys, onlyKeys],
  );

  const multipleWordMatchSorter = useCallback(
    (items: readonly T[], input: string, options: Record<string, unknown>) => {
      const words = input.split(" ");

      return words.reduceRight((data, word) => matchSorter(data, word, options), items) as T[];
    },
    [],
  );

  const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (event) => {
      const input = event.target.value;

      processInput(multipleWordMatchSorter, input, { keys });

      setValue(input);
      setShowDelete(!!input);
    },
    [keys, multipleWordMatchSorter, processInput],
  );

  const handleDelete = useCallback(() => {
    reset();

    setValue("");
    setShowDelete(false);
  }, [reset]);

  return (
    <StyledTextField
      {...rest}
      variant="outlined"
      value={value}
      placeholder={formatMessage("search")}
      onChange={handleChange}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">{<SearchIcon icon="search" size={16} />}</InputAdornment>,
          endAdornment: <ClearIcon $show={showDelete} icon="delete-left" size={16} onClick={handleDelete} />,
          className: inputClassName,
        },
        htmlInput: {
          className: htmlInputClassName,
        },
      }}
      autoComplete="off"
    />
  );
};

const formatStrings = {
  search: {
    en: "Search",
  },
} satisfies FormatMessages;
